// src/components/WarRoomCaseFiles.js
//
// RULING 309 (Bob 2026-10-12): "the general chairs warroom, create an area and a box to drop old case files into for review,
// analytics, assessments, a thorough critical assessment, but balanced fairly and even handedly - just honest but real too".
//
// The box: drop a PDF (scanned or not, any length the storage takes). It goes to warRoomCaseFiles/{committee}/ in Storage
// and a record in warRoomCaseFiles (status 'queued'); the server reads it once the malware scan marks it clean, in
// sections, and writes the review, the timeline analytics and the assessment back onto the record (functions
// assessCaseFileOnQueue). The General Chairperson of the committee and, during the build, the administrator - nobody else.
// Nothing is deleted; a file may be assessed again and every assessment is kept.
import React, { useEffect, useState } from 'react';
import { db, storage } from '../firebase';
import { collection, query, where, onSnapshot, addDoc, doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { ref as sref, uploadBytes } from 'firebase/storage';
import RulingRef from './RulingRef';
import { newCaseFileRecord, MAX_FILE_BYTES, BOX_NOTE } from '../data/caseFileReview';
import { SlidesButton } from './SlideDeck';   // ruling 311: the assessment as slides
import { caseFileDeck, patternsDeck } from '../data/slideDecks';

const C = { panel: '#0B1626', line: '#2E4A6B', dim: '#8AA4BF', accent: '#E8A020', bad: '#E06C6C', ok: '#5FB878', text: '#DCE7F2' };
const STATUS_WORDS = {
  queued: 'Queued', waiting_scan: 'Waiting for the malware scan', reading: 'Reading', assessed: 'Assessed', failed: 'Not assessed',
};
const when = (t) => { try { const d = t && t.toDate ? t.toDate() : (t ? new Date(t) : null); return d ? d.toISOString().slice(0, 16).replace('T', ' ') : ''; } catch (e) { return ''; } };

export default function WarRoomCaseFiles({ user, committee }) {
  const [rows, setRows] = useState(null);
  const [patterns, setPatterns] = useState([]);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState('');
  const [drag, setDrag] = useState(false);
  // Forensic audit 2026-10-16: each read's failure, apart from `err` (a drop's or a request's). A failed read of the
  // files used to show the error AND "No case files yet."; a failed read of the pattern requests was silent, with
  // "Find the patterns" live over a request that may already be queued.
  const [filesErr, setFilesErr] = useState('');
  const [patternsErr, setPatternsErr] = useState('');

  useEffect(() => {
    if (!committee) return undefined;
    setFilesErr(''); setPatternsErr('');
    const q = query(collection(db, 'warRoomCaseFiles'), where('committee', '==', committee));
    const u1 = onSnapshot(q, (s) => { setRows(s.docs.map(d => ({ id: d.id, ...d.data() }))
      .sort((a, b) => String(when(b.uploadedAt)).localeCompare(String(when(a.uploadedAt))))); setFilesErr(''); },
      (e) => { setFilesErr('Could not read the case files: ' + (e.code || e.message) + '. This is NOT "no case files" - reload the page.'); setRows(null); });
    const q2 = query(collection(db, 'warRoomCasePatterns'), where('committee', '==', committee));
    const u2 = onSnapshot(q2, (s) => { setPatterns(s.docs.map(d => ({ id: d.id, ...d.data() }))
      .sort((a, b) => String(when(b.requestedAt)).localeCompare(String(when(a.requestedAt))))); setPatternsErr(''); },
      (e) => { setPatterns([]); setPatternsErr('Could not read the pattern readings: ' + ((e && (e.code || e.message)) || 'refused') + '. Whether one is already queued or done is unknown, so none can be asked for until they can be read.'); });
    return () => { u1(); u2(); };
  }, [committee]);

  const drop = async (files) => {
    setErr(''); setMsg('');
    const list = Array.from(files || []);
    if (!list.length) return;
    setBusy(true);
    let done = 0;
    for (const f of list) {
      const safe = String(f.name).replace(/[^A-Za-z0-9._ -]/g, '_').slice(0, 150);
      const path = 'warRoomCaseFiles/' + committee + '/' + Date.now() + '_' + safe;
      const r = newCaseFileRecord({ committee, title: f.name.replace(/\.pdf$/i, ''), fileName: f.name, storagePath: path,
        sizeBytes: f.size, user, serverTime: serverTimestamp() });
      if (!r.ok) { setErr(f.name + ': ' + r.errors.join(' ')); continue; }
      try {
        await uploadBytes(sref(storage, path), f, { contentType: 'application/pdf', customMetadata: { uploadedBy: user.uid } });
        await addDoc(collection(db, 'warRoomCaseFiles'), r.doc);
        done++;
      } catch (e) {
        setErr(f.name + ' was not dropped: ' + (e.code || e.message) + '. Nothing was recorded for it.');
      }
    }
    setBusy(false);
    if (done) setMsg(done + ' file' + (done === 1 ? '' : 's') + ' dropped. Each is scanned for malware, then read and assessed - a long scanned file takes several minutes.');
  };

  const again = async (row) => {
    setErr('');
    try { await updateDoc(doc(db, 'warRoomCaseFiles', row.id), { status: 'queued', requestedBy: user.uid, requestedAt: serverTimestamp() }); }
    catch (e) { setErr('Not queued: ' + (e.code || e.message)); }
  };
  const askPatterns = async () => {
    setErr('');
    try { await addDoc(collection(db, 'warRoomCasePatterns'), { committee, requestedBy: user.uid, requestedAt: serverTimestamp(), status: 'queued' }); }
    catch (e) { setErr('Not requested: ' + (e.code || e.message)); }
  };

  if (!committee) return null;
  const assessed = (rows || []).filter(r => r.status === 'assessed').length;
  const latest = patterns[0];
  return (
    <div style={{ background: C.panel, border: '1px solid ' + C.line, borderRadius: 8, padding: '14px 16px', marginBottom: 20 }}>
      <div style={{ fontSize: 15, fontWeight: 700, color: C.text }}>
        Past case files - review and assessment
        <RulingRef user={user} n={309} why="Old case files dropped here are reviewed, analysed and assessed - critically, but balanced and honest - for the General Chairperson alone." />
      </div>
      <div style={{ fontSize: 12, color: C.dim, margin: '4px 0 10px' }}>{BOX_NOTE}</div>

      <label
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }} onDragLeave={() => setDrag(false)}
        onDrop={(e) => { e.preventDefault(); setDrag(false); if (!busy) drop(e.dataTransfer.files); }}
        style={{ display: 'block', border: '2px dashed ' + (drag ? C.accent : C.line), borderRadius: 8, padding: '18px 14px', textAlign: 'center', color: C.text, cursor: busy ? 'default' : 'pointer', marginBottom: 10 }}>
        {busy ? 'Dropping...' : 'Drop old case files here (PDF), or click to choose. Up to ' + Math.round(MAX_FILE_BYTES / 1048576) + ' MB each.'}
        <input aria-label="Attach case files (PDF)" type="file" accept="application/pdf,.pdf" multiple disabled={busy} style={{ display: 'none' }}
          onChange={(e) => { drop(e.target.files); e.target.value = ''; }} />
      </label>
      {err ? <div style={{ fontSize: 12, color: C.bad, marginBottom: 8 }}>{err}</div> : null}
      {msg ? <div style={{ fontSize: 12, color: C.ok, marginBottom: 8 }}>{msg}</div> : null}

      {filesErr ? <div style={{ fontSize: 12, color: C.bad }}>&#9888; {filesErr}</div>
        : rows === null ? <div style={{ fontSize: 12, color: C.dim }}>Reading the case files...</div>
        : rows.length === 0 ? <div style={{ fontSize: 12, color: C.dim }}>No case files yet.</div>
        : rows.map(r => (
          <div key={r.id} style={{ borderTop: '1px solid ' + C.line, padding: '8px 0' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap', alignItems: 'baseline' }}>
              <div style={{ color: C.text, fontSize: 13, fontWeight: 600 }}>{r.title || r.fileName}</div>
              <div style={{ fontSize: 11, color: r.status === 'failed' ? C.bad : r.status === 'assessed' ? C.ok : C.accent }}>
                {STATUS_WORDS[r.status] || r.status}
                {r.status === 'reading' && r.sections ? ' - section ' + Math.min((r.sectionsDone || 0) + 1, r.sections) + ' of ' + r.sections + (r.sectionsDone >= r.sections ? ' (writing the assessment)' : '') : ''}
                {r.pageCount ? ' - ' + r.pageCount + ' pages' : ''}
              </div>
            </div>
            <div style={{ fontSize: 11, color: C.dim }}>
              Dropped {when(r.uploadedAt)}{r.uploadedByName ? ' by ' + r.uploadedByName : ''}
              {r.assessedAt ? ' - assessed ' + when(r.assessedAt) : ''}
              {Array.isArray(r.assessments) && r.assessments.length > 1 ? ' - ' + r.assessments.length + ' assessments kept' : ''}
            </div>
            {r.failure ? <div style={{ fontSize: 12, color: C.bad, marginTop: 4 }}>{r.failure}</div> : null}
            <div style={{ marginTop: 6, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {r.assessment ? <button className="btn btn-secondary btn-sm" onClick={() => setOpen(open === r.id ? '' : r.id)}>{open === r.id ? 'Hide the assessment' : 'Read the assessment'}</button> : null}
              {r.assessment ? <SlidesButton build={() => caseFileDeck(r)} /> : null}   {/* ruling 311 */}
              {(r.status === 'assessed' || r.status === 'failed') ? <button className="btn btn-secondary btn-sm" onClick={() => again(r)}>Assess again</button> : null}
            </div>
            {open === r.id && r.assessment ? (
              <div style={{ whiteSpace: 'pre-wrap', fontSize: 13, lineHeight: 1.55, color: C.text, background: '#08111D', border: '1px solid ' + C.line, borderRadius: 6, padding: '12px 14px', marginTop: 8 }}>{r.assessment}</div>
            ) : null}
          </div>
        ))}

      <div style={{ borderTop: '1px solid ' + C.line, marginTop: 8, paddingTop: 10 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>Patterns across the files</div>
        <div style={{ fontSize: 12, color: C.dim, margin: '2px 0 8px' }}>Read across every assessed file of the committee ({filesErr ? 'count unknown - the files could not be read' : assessed + ' so far'}; at least two are needed).</div>
        {patternsErr ? <div style={{ fontSize: 12, color: C.bad, marginBottom: 6 }}>&#9888; {patternsErr}</div> : null}
        <button className="btn btn-secondary btn-sm" disabled={!!filesErr || !!patternsErr || assessed < 2 || (latest && latest.status === 'queued')} onClick={askPatterns}>
          {latest && latest.status === 'queued' ? 'Reading the files...' : 'Find the patterns'}
        </button>
        {latest && latest.status === 'failed' ? <div style={{ fontSize: 12, color: C.bad, marginTop: 6 }}>{latest.failure}</div> : null}
        {latest && latest.text ? <span style={{ marginLeft: 8 }}><SlidesButton build={() => patternsDeck(latest)} /></span> : null}   {/* ruling 311 */}
        {latest && latest.text ? (
          <div style={{ whiteSpace: 'pre-wrap', fontSize: 13, lineHeight: 1.55, color: C.text, background: '#08111D', border: '1px solid ' + C.line, borderRadius: 6, padding: '12px 14px', marginTop: 8 }}>
            <div style={{ fontSize: 11, color: C.dim, marginBottom: 6 }}>{latest.files} files - {when(latest.assessedAt)}</div>
            {latest.text}
          </div>
        ) : null}
      </div>
    </div>
  );
}
